import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import './MarkToMarketPage.css';

const MarkToMarketPage = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [markToMarketData, setMarkToMarketData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [error, setError] = useState(null);

  // Fetch mark-to-market data on component mount
  useEffect(() => {
    console.log('MarkToMarketPage: Component mounted, fetching data...');
    fetchMarkToMarketData();
    fetchStatistics();
  }, []);

  const fetchMarkToMarketData = async () => {
    try {
      console.log('MarkToMarketPage: Fetching mark-to-market data...');
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('MarkToMarketPage: Token exists:', !!token);
      const response = await axios.get(`${API_BASE_URL}/mark-to-market/data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('MarkToMarketPage: API response:', response.data);

      if (response.data.success) {
        setMarkToMarketData(response.data.data);
        setError(null);
      } else {
        setError('Failed to fetch mark-to-market data');
      }
    } catch (error) {
      console.error('MarkToMarketPage: Error fetching mark-to-market data:', error);
      console.error('MarkToMarketPage: Error details:', error.response?.data);
      setError('Error fetching mark-to-market data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/mark-to-market/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/octet-stream'
      ];
      
      const allowedExtensions = ['.xls', '.xlsx'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension)) {
        setUploadedFile(file);
        setError(null);
      } else {
        setError('Please select a valid Excel file (.xls or .xlsx)');
        setUploadedFile(null);
      }
    }
  };

  const handleFileUpload = async () => {
    if (!uploadedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadResult(null);

      const formData = new FormData();
      formData.append('excelFile', uploadedFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/mark-to-market/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setUploadResult(response.data);
        // Refresh data after successful upload
        await fetchMarkToMarketData();
        await fetchStatistics();
        setUploadedFile(null);
        // Reset file input
        document.getElementById('excelFile').value = '';
      } else {
        setError('Upload failed: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Upload failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatNumber = (value, decimals = 4) => {
    if (value === null || value === undefined) return '-';
    return parseFloat(value).toFixed(decimals);
  };

  console.log('MarkToMarketPage: Rendering with state:', { loading, error, markToMarketData: markToMarketData.length, statistics });

  return (
    <div className="mark-to-market-page">
      <div className="page-header">
        <h1>Mark-to-Market Management</h1>
      
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="statistics-cards">
          <div className="stat-card">
            <h3>Total Records</h3>
            <p className="stat-number">{statistics.total_records}</p>
          </div>
          <div className="stat-card">
            <h3>Unique Series</h3>
            <p className="stat-number">{statistics.unique_series}</p>
          </div>
          <div className="stat-card">
            <h3>Average Yield</h3>
            <p className="stat-number">{formatNumber(statistics.avg_yield, 2)}%</p>
          </div>
          <div className="stat-card">
            <h3>Average Price</h3>
            <p className="stat-number">{formatNumber(statistics.avg_price, 2)}</p>
          </div>
          {statistics.tbill_records != null && (
            <div className="stat-card">
              <h3>T-Bill Records</h3>
              <p className="stat-number">{statistics.tbill_records || 0}</p>
            </div>
          )}
          {statistics.interpolated_records != null && (
            <div className="stat-card">
              <h3>Interpolated</h3>
              <p className="stat-number">{statistics.interpolated_records || 0}</p>
            </div>
          )}
        </div>
      )}

      {/* File Upload Section */}
      <div className="upload-section">
        <h2>Upload Excel File</h2>
        <div className="upload-container">
          <div className="file-input-container">
            <input
              type="file"
              id="excelFile"
              accept=".xls,.xlsx"
              onChange={handleFileChange}
              className="file-input"
            />
            <label htmlFor="excelFile" className="file-input-label">
              {uploadedFile ? uploadedFile.name : 'Choose Excel File (.xls, .xlsx)'}
            </label>
          </div>
          
          <button
            onClick={handleFileUpload}
            disabled={!uploadedFile || uploading}
            className="upload-button"
          >
            {uploading ? 'Uploading...' : 'Upload & Process'}
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {uploadResult && (
          <div className="success-message">
            <span className="success-icon">✅</span>
            <div>
              <strong>Upload Successful!</strong>
              <p>File: {uploadResult.data.filename}</p>
              <p>Records Processed: {uploadResult.data.recordsProcessed}</p>
              {uploadResult.data.bondRecords != null && (
                <p>T-Bond quotes: {uploadResult.data.bondRecords} | T-Bill quotes: {uploadResult.data.billRecords}</p>
              )}
              <p>Success: {uploadResult.data.updateResults.successCount}</p>
              <p>Interpolated: {uploadResult.data.updateResults.interpolatedCount || 0}</p>
              <p>Errors: {uploadResult.data.updateResults.errorCount}</p>
              <p>Skipped: {uploadResult.data.updateResults.skippedCount}</p>
            </div>
          </div>
        )}
      </div>

      {/* Data Display Section */}
      <div className="data-section">
        <div className="section-header">
          <h2>Mark-to-Market Data</h2>
          <button onClick={fetchMarkToMarketData} className="refresh-button" disabled={loading}>
            {loading ? 'Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {loading ? (
          <div className="loading-message">
            <span className="loading-spinner">⏳</span>
            Loading mark-to-market data...
          </div>
        ) : markToMarketData.length === 0 ? (
          <div className="no-data-message">
            <span className="no-data-icon">📊</span>
            No mark-to-market data available. Upload an Excel file to get started.
          </div>
        ) : (
          <div className="data-table-container">
            <table className="mark-to-market-table">
              <thead>
                <tr>
                  <th>Series</th>
                  <th>ISIN Number</th>
                  <th>Instrument</th>
                  <th>Quote Source</th>
                  <th>ISIN Issuer</th>
                  <th>Maturity Date</th>
                  <th>Buying Price</th>
                  <th>Selling Price</th>
                  <th>Average Price</th>
                  <th>Buying Yield</th>
                  <th>Selling Yield</th>
                  <th>Average Yield</th>
                  <th>Dirty Price</th>
                  <th>Last Updated</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {markToMarketData.map((record, index) => (
                  <tr key={record.id || index}>
                    <td className="series-cell">{record.series}</td>
                    <td className="isin-cell">{record.isin_number}</td>
                    <td>{record.instrument_type || '-'}</td>
                    <td>{record.quote_source || '-'}</td>
                    <td className="issuer-cell">{record.isin_issuer}</td>
                    <td className="date-cell">{formatDate(record.maturity_date)}</td>
                    <td className="price-cell">{formatNumber(record.buying_price)}</td>
                    <td className="price-cell">{formatNumber(record.selling_price)}</td>
                    <td className="price-cell average">{formatNumber(record.average_price)}</td>
                    <td className="yield-cell">{formatNumber(record.buying_yield, 2)}%</td>
                    <td className="yield-cell">{formatNumber(record.selling_yield, 2)}%</td>
                    <td className="yield-cell average">{formatNumber(record.average_yield, 2)}%</td>
                    <td className="dirty-price-cell">{formatNumber(record.dirty_price)}</td>
                    <td className="date-cell">{formatDate(record.last_updated)}</td>
                    <td className="source-cell">{record.excel_source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkToMarketPage;
